'use client';
import { usePathname, useRouter } from 'next/navigation';
import { PropsWithChildren, useEffect } from 'react';

import styled from '@emotion/styled';
import { useBoolean } from 'usehooks-ts';

import { Footer } from '@web/features/Footer';
import { Header } from '@web/features/Header';
import { SideSheet } from '@web/features/side-sheet/SideSheet';
import {
  SPIKE_MESSAGE_ENABLED,
  SpikeMessage,
} from '@web/features/spike-message';
import { SplashGuard } from '@web/features/SplashScreen/SplashGuard';
import { useBridgeManager } from '@web/features/staking/BridgeManager';
import { BridgePopupMinimized } from '@web/features/staking/BridgePopupMinimized';
import { isUserCompleted, useCurrentUser } from '@web/features/user/service';
import { env } from '@web/utils/env';

const FullHeightContainer = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
`;

const BridgePopups = styled.div`
  position: fixed;
  display: flex;
  flex-direction: column;
  right: ${({ theme }) => theme.spacing('1500')};
  bottom: ${({ theme }) => theme.spacing('1500')};
  left: ${({ theme }) => theme.spacing('1500')};
  gap: ${({ theme }) => theme.spacing('0500')};
  z-index: 10;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    left: unset;
  }
`;

const skipHeaderFor = [
  '/collection',
  '/the-room',
  '/council',
  '/lore',
  '/valley',
];
const skipSplashFor = ['/profile'];

export default ({ children }: PropsWithChildren) => {
  const {
    value: isSideSheetOpen,
    setTrue: openSideSheet,
    setFalse: closeSideSheet,
  } = useBoolean(false);
  const currentUser = useCurrentUser();
  const completedUser = isUserCompleted(currentUser) ? currentUser : undefined;

  const pathname = usePathname();
  const isHub = pathname === `/`;
  const skipHeader = skipHeaderFor.includes(pathname);
  const skipSplash = skipSplashFor.includes(pathname);
  // Chapter readers carry their own bottom control bar in that corner.
  const isMangaChapter = pathname.startsWith('/manga/');
  const router = useRouter();
  const { pendingBridges } = useBridgeManager();

  useEffect(() => {
    if (currentUser.status === 'connected' && !currentUser.user.twitter) {
      return router.replace('/login/connect-x');
    }
  }, [currentUser]);

  return (
    <SplashGuard guardIf={!skipSplash && currentUser.status === 'pending'}>
      <FullHeightContainer>
        {!skipHeader && (
          <Header
            user={completedUser?.user}
            enableNavigateBack={!isHub}
            onOpenSideSheet={openSideSheet}
            onNavigateBack={router.back}
          />
        )}
        <SideSheet
          user={completedUser?.user}
          open={isSideSheetOpen}
          onClose={closeSideSheet}
        />
        {children}
        <BridgePopups>
          {pendingBridges.map((pendingBridge) => (
            <BridgePopupMinimized
              key={pendingBridge.data.id}
              pendingBridge={pendingBridge}
            />
          ))}
        </BridgePopups>
        {env.SHELL_MODE && SPIKE_MESSAGE_ENABLED && !isMangaChapter && (
          <SpikeMessage />
        )}
        <Footer />
      </FullHeightContainer>
    </SplashGuard>
  );
};
